import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { BackButton } from '@/components/common/BackButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import {
  booklistSheetQueryOptions,
  requisitionQueryKeys,
  saveBooklistAnswers,
} from '../api/productRequisition.api';
import { BooklistSheetTable } from '../components/BooklistSheetTable';
import { useSheetDraft } from '../hooks/useSheetDraft';
import { toBooklistDraft, type BooklistDraft } from '../utils/sheetDrafts';
import { RequisitionFilterPanel } from '../components/RequisitionFilterPanel';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import { BOOKLIST_QUESTIONS, type BooklistSheetSearch } from '../types/productRequisition.types';
import { Can } from '@/components/common/Can';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';

const route = getRouteApi('/_auth/product/booklisted-institution/$projectId');
const LIST_ROUTE = '/product/booklisted-institution';

const NUMERIC_KEYS = ['bookEditionId', 'instituteTypeId', 'thanaId'] as const;

export function BooklistEntryPage() {
  const { projectId } = route.useParams();
  const queryClient = useQueryClient();
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
  });
  const params = controller.params as BooklistSheetSearch;

  const { data, isLoading, error, refetch } = useQuery(
    booklistSheetQueryOptions(projectId, params),
  );

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [draft, setDraft] = useSheetDraft<typeof data, BooklistDraft>(
    data,
    (sheet) => (sheet === undefined ? {} : toBooklistDraft(sheet)),
    {},
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const editionId = params.bookEditionId;
      if (editionId === undefined) throw new Error('No edition picked');

      const answers = (data?.institutes ?? [])
        .filter((institute) => institute.answers?.isReview !== true)
        .map((institute) => ({
          instituteId: institute.instituteId,
          ...(draft[institute.instituteId] ?? {}),
        }));

      if (answers.length === 0) throw new Error('Nothing to save');
      return saveBooklistAnswers(projectId, editionId, answers);
    },
    onSuccess: (saved) => {
      toast.success(
        saved.locked > 0
          ? `Saved ${saved.stored}; ${saved.locked} already reviewed and left alone`
          : `Saved ${saved.stored} institute(s)`,
      );
      void queryClient.invalidateQueries({ queryKey: requisitionQueryKeys.all() });
    },
    onError: () => toast.error('Could not save the sheet'),
  });

  const yesCounts = useMemo(() => {
    const lines = Object.values(draft);
    return BOOKLIST_QUESTIONS.map((question) => ({
      key: question.key,
      label: question.label,
      value: lines.filter((line) => line[question.key]).length,
    }));
  }, [draft]);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={data?.projectName ?? 'Booklisted Institution'}
        leading={<BackButton onClick={() => void routeNavigate({ to: LIST_ROUTE })} />}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <Can code={PRODUCT_PERMISSIONS.BOOKLISTED_INSTITUTE_EDIT}>
              <Button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || data === undefined || !(data?.isOpen ?? false)}
              >
                Save Sheet
              </Button>
            </Can>
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <RequisitionFilterPanel filters={controller} withGeo withInstituteType withBook />
      </FilterPanelCollapse>

      {params.bookEditionId === undefined ? (
        <EmptyState
          title="Pick a book edition"
          description="The six answers are given per edition, so choose one to open the sheet."
        />
      ) : (
        <>
          <StatCardGrid stats={yesCounts} isLoading={isLoading} isError={error !== null} />

          {error ? (
            <ErrorState
              error={error}
              fallbackTitle="Could not load the sheet"
              onRetry={() => void refetch()}
              fill
            />
          ) : isLoading ? (
            <ListSkeleton />
          ) : data === undefined || data.institutes.length === 0 ? (
            <EmptyState
              title="No institute to sheet"
              description="No institute is assigned to you under this type and thana."
            />
          ) : (
            <BooklistSheetTable
              sheet={data}
              draft={draft}
              onChange={(instituteId, answers) =>
                setDraft((previous) => ({ ...previous, [instituteId]: answers }))
              }
            />
          )}
        </>
      )}
    </div>
  );
}
